import { Injectable } from '@angular/core';
import { IFacade } from '@core/store/facade';
import { ClassroomIcon } from '@models/classroom-icon';
import { KeyValuePair } from '@models/key-value-pair';
import { SchoolLicense } from '@models/license/school-license';
import { School } from '@models/school';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { loadUserSchoolLicenses, SCHOOL_ACTIONS, setUserSchoolLicense } from './schools.actions';
import { SchoolsState } from './schools.reducer';
import {
  selectClassroomIcons,
  selectClassroomIconsUnloaded,
  selectSchoolById,
  selectSchools,
  selectSchoolsCanRefresh,
  selectSelectedSchoolId,
  selectSchoolsState,
  selectSchoolLicenses,
} from './schools.selectors';

@Injectable({ providedIn: 'root' })
export class SchoolsFacade implements IFacade {
  schools$: Observable<School[]>;
  schoolsCanRefresh$: Observable<boolean>;
  selectedSchoolId$: Observable<string>;
  classroomIcons$: Observable<ClassroomIcon[]>;
  canRefreshClassroomIcons$: Observable<boolean>;
  schoolsState$: Observable<SchoolsState>;
  schoolLicenses$: Observable<KeyValuePair<SchoolLicense[]>[]>;

  constructor(private store: Store<{ schools: SchoolsState }>) {
    this.schools$ = store.select(selectSchools);
    this.schoolsCanRefresh$ = store.select(selectSchoolsCanRefresh);
    this.selectedSchoolId$ = store.select(selectSelectedSchoolId);
    this.classroomIcons$ = store.select(selectClassroomIcons);
    this.canRefreshClassroomIcons$ = store.select(selectClassroomIconsUnloaded);
    this.schoolsState$ = store.select(selectSchoolsState);
    this.schoolLicenses$ = store.select(selectSchoolLicenses);
  }

  get(schoolId: string): Observable<School> {
    return this.selectWithProps(selectSchoolById, schoolId);
  }

  loadSchools() {
    this.schools$.pipe(first()).subscribe((schools) => {
      if (schools?.length === 0) {
        this.schoolsCanRefresh$.pipe(first()).subscribe((canRefresh) => {
          if (canRefresh) {
            this.dispatch({ type: SCHOOL_ACTIONS.refresh });
          }
        });
      }
    });
  }

  loadUserSchoolLicenses(schools: School[]) {
    this.store.dispatch(loadUserSchoolLicenses({ schools }));
  }

  setUserSchoolLicenses(schoolLicensePairs: KeyValuePair<SchoolLicense[]>[]) {
    this.store.dispatch(setUserSchoolLicense({ schoolLicensePairs }));
  }

  schoolsInDistrict(districtId: string): Observable<School[]> {
    return this.schools$.pipe(
      map(schools => schools.filter(s => s.districtId === districtId))
    );
  }

  /**
   * @deprecated keep dispatch calls encapsulated inside the facade.
   */
  dispatch(action: Action): void {
    this.store.dispatch(action);
  }

  selectWithProps(func: Function, ...props: any[]): Observable<any> {
    return this.store.select(func(...props));
  }
}
